import { Visualizer, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { Color } from '../svg/svg';
import { Map as TLMDMap } from '../map/map';
import { Timeline } from '../timeline/timeline';
import { Drawer } from '../panel/cabinets';
import { replaceNodeContent, eventObserver, delegateObserver } from '../dom/dom';
import { xhrPromise, Method, ResponseType } from '../xhr/xhr';
import { createEditor, destroyEditor, getEditorValue } from './editor';

import { ObservationDataSource } from './observation';
import { ObservationContainer, CallbackDefinition, CallbackDataProperties, ObservationDatum, EnumMapEntry } from './container-observation';

interface TicketingDefinition {
  id: string,
  label: string,
  driver: string,
  active: boolean,
  deleted: boolean,
  states: TicketState[],
  results: TicketResult[],
  statesMap: { [key: string]: { changeOwner?: string[], changeResult?: string[] } },
  resultsMap: { [key: string]: { motivations?: string[] } },
  enumMap: { [key: string]: EnumMapEntry }
}
interface TicketState {
  id: string,
  label: string,
  successors: string[],
  color?: Color
}
interface TicketResult {
  id: string,
  label: string,
  color?: Color
}

interface TicketingDataProperties extends CallbackDataProperties {
  ticket?: string,
  result?: {
    time: string,
    status: string,
    owner: string,
    result: string,
    motivation: string
  }
}
export interface TicketingCallbackDefinition extends CallbackDefinition {
  parameters: {
    type: string
  },
  definition: TicketingDefinition
}

export class ObservationTicketingVisualizer implements Visualizer {

  private inspectionContainer: ObservationContainer;
  private source: ObservationDataSource;
  private callbackDefinition: TicketingCallbackDefinition;
  private ticketingDefinition: TicketingDefinition;
  private ticketId: string;


  public constructor(private visualizerKey: string, private obsType: string, private drawer: Drawer, _exportAllowed: boolean) {
    this.inspectionContainer = <ObservationContainer>this.drawer.getContainer();
    this.source = this.inspectionContainer.getDataSource();
    let typeDefinition = this.inspectionContainer.getDefinition();

    this.callbackDefinition = <TicketingCallbackDefinition>typeDefinition.callbacks.filter(callbackDefinition => callbackDefinition.processor === 'ticketing')[0];

    this.ticketingDefinition = this.callbackDefinition.definition;
    this.ticketId = undefined;
    this.subscribeHandlers();

  }

  public release(): void {
    this.releaseEditors();
  }

  public key(): string {
    return this.visualizerKey + '_' + this.type();
  }

  public type(): string {
    return this.obsType;
  }

  public setControls(_controls: { [key: string]: VisualizerControl }): Visualizer {
    //no controls here
    return this;
  }

  public setTimeline(_timeline: Timeline): void {
    //we do not deal with the timeline here
  }

  public setMap(_map: TLMDMap): void {
    //we do not deal with the map here
  }


  public updateSelection(): void {
    let dataList = this.source.getData()[this.type()];
    let selectionSize = dataList.getSelectionSize();
    if (selectionSize === 1) {
      this.updateTicketing(<ObservationDatum>dataList.getSelection()[0]);
    }
  }

  public refresh(_level: VisualizationUpdateLevel): void {
    //no refreshing needed by this visualizer
  }

  private updateTicketing(datum: ObservationDatum): void {
    if (datum.callbacks && datum.callbacks.ticketing) {
      if (this.ticketingDefinition) {
        let callbackData = <TicketingDataProperties>datum.callbacks.ticketing;
        this.ticketId = callbackData.ticket;
        let sectionElement = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing');

        xhrPromise(
          {
            url: `/${this.inspectionContainer.getApplication().getDomain()}/observation/inspection/ticketing/${this.type()}/${this.ticketingDefinition.id}${this.ticketId ? `/${this.ticketId}` : ''}`,
//            url: `/${this.inspectionContainer.getApplication().getDomain()}/observation/inspection/ticketing${this.ticketId ? `/${this.ticketId}` : ''}`,
            method: Method.get,
            accept: 'text/html',
            responseType: ResponseType.document,
            digester: this.inspectionContainer.getApplication().getDigester()
          }
        ).then((response) => {
          let htmlElem = <HTMLElement>response.data;
          this.releaseEditors();
          replaceNodeContent(this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing'), htmlElem);
          this.createEditors();
          this.updateStatusEditor();
          this.updateMotivationOptions(this.value((<HTMLDivElement>sectionElement.querySelector('div.edit-bubbles')).dataset.result));
        }).catch((error) => {
          console.error(`Unable to visualize ticketing: Ticket ${callbackData.ticket} not loaded.`, error);
        });

      } else {
        console.info(`Unable to visualize ticketing: Ticketing definition not loaded.`)
      }
    } else {
      console.info(`Unable to visualize ticketing: Datum ${datum.id} holfds no ticket data.`)
    }
  }


  private releaseEditors(): void {
    let editorsDiv = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing div.edit-bubbles');
    if (editorsDiv) {
      let statusEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.status div.editor');
      let memoEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.memo div.editor');
      let observermessageEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.observermessage div.editor');
      let ownermessageEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.ownermessage div.editor');

      //destroy the editors
      if (statusEditor) destroyEditor(statusEditor);
      if (memoEditor) destroyEditor(memoEditor);
      if (observermessageEditor) destroyEditor(observermessageEditor);
      if (ownermessageEditor) destroyEditor(ownermessageEditor);
    }
  }

  private createEditors(): void {
    let editorsDiv = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing div.edit-bubbles');
    if (editorsDiv) {
      let statusEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.status div.editor');
      let memoEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.memo div.editor');
      let observermessageEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.observermessage div.editor');
      let ownermessageEditor = <HTMLDivElement>editorsDiv.querySelector('div.tlmd-ticketing-editor.ownermessage div.editor');

      //destroy the editors
      if (statusEditor) createEditor(statusEditor);
      if (memoEditor) createEditor(memoEditor);
      if (observermessageEditor) createEditor(observermessageEditor);
      if (ownermessageEditor) createEditor(ownermessageEditor);
    }
  }

  private subscribeHandlers(): void {
    let sectionElement = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing');
    if (sectionElement) {
      
      let clicks = eventObserver(this.drawer.getContainerNode(), 'click', { passive: false });
      let changes = eventObserver(this.drawer.getContainerNode(), 'change', { passive: true });

      this.inspectionContainer.addSubscription(delegateObserver(this.drawer.getContainerNode(), clicks, `div.tlmd-content.ticketing div.buttons a.tlmd-button`)
        .subscribe(evAndTrgt => {
          evAndTrgt.event.preventDefault();
          let editorName = evAndTrgt.target.dataset.name;
          let editorDiv = <HTMLDivElement>sectionElement.querySelector(`div.edit-bubbles div.tlmd-ticketing-editor.${editorName}`);
          editorDiv.parentNode.querySelectorAll('div.tlmd-ticketing-editor').forEach(btn => btn.classList.remove('tlmd-selected'));
          editorDiv.classList.add('tlmd-selected');
          (<HTMLDivElement>sectionElement.querySelector('div.buttons')).classList.add('tlmd-disabled');
        })
      );

      // hide/show other status editor select boxes based on status value
      this.inspectionContainer.addSubscription(delegateObserver(this.drawer.getContainerNode(), changes, `div.tlmd-content.ticketing div.edit-bubbles div.tlmd-ticketing-editor.status select#ticket-status-edit`)
        .subscribe(_evAndTrgt => {
          this.updateStatusEditor();
        })
      );
      // update set motivation options based on selected result value
      this.inspectionContainer.addSubscription(delegateObserver(this.drawer.getContainerNode(), changes, `div.tlmd-content.ticketing div.edit-bubbles div.tlmd-ticketing-editor.status select#ticket-result-edit`)
        .subscribe(evAndTrgt => {
          let resultControl = (<HTMLSelectElement>evAndTrgt.target);
          this.updateMotivationOptions(resultControl.value);
        })
      );
      this.inspectionContainer.addSubscription(delegateObserver(this.drawer.getContainerNode(), clicks, `div.tlmd-content.ticketing div.edit-bubbles a.tlmd-button`)
        .subscribe(evAndTrgt => {
          evAndTrgt.event.preventDefault();
          let action = evAndTrgt.target.dataset.name;
          let editorDiv = <HTMLDivElement>evAndTrgt.target.parentElement.parentElement;
          if (action === 'cancel') {
            (<HTMLDivElement>sectionElement.querySelector('div.buttons')).classList.remove('tlmd-disabled');
            editorDiv.classList.remove('tlmd-selected');
          } else {
            let editorName = editorDiv.dataset.name;
            let contentEditor = <HTMLDivElement>editorDiv.querySelector('div.editor');

            let content = contentEditor ? getEditorValue(contentEditor) : null;

            let statusControl = <HTMLSelectElement>editorDiv.querySelector('select#ticket-status-edit');
            let ownerControl = <HTMLSelectElement>editorDiv.querySelector('select#ticket-owner-edit');
            let resultControl = <HTMLSelectElement>editorDiv.querySelector('select#ticket-result-edit');
            let motivationControl = <HTMLSelectElement>editorDiv.querySelector('select#ticket-motivation-edit');

            let editorsDiv = <HTMLDivElement>sectionElement.querySelector('div.edit-bubbles');

            let newStatus = statusControl ? statusControl.value : this.value(editorsDiv.dataset.status);

            let newOwner = ownerControl && this.ownerCanChange(this.value(editorsDiv.dataset.status), newStatus) ? ownerControl.value : this.value(editorsDiv.dataset.owner);
            let newResult = resultControl && this.resultCanChange(this.value(editorsDiv.dataset.status), newStatus) ? resultControl.value : this.value(editorsDiv.dataset.result);
            let newMotivation = motivationControl && this.resultCanChange(this.value(editorsDiv.dataset.status), newStatus) ? motivationControl.value : this.value(editorsDiv.dataset.motivation);

            let logEntry = { content: content, status: newStatus, owner: newOwner, result: newResult, motivation: newMotivation, type: editorName };

            xhrPromise(
              {
                url: `/${this.inspectionContainer.getApplication().getDomain()}/observation/inspection/ticketing/${this.type()}/${this.ticketingDefinition.id}/${editorName}/${this.ticketId}`,
                method: Method.post,
                data: { entity: JSON.stringify(logEntry), mimeType: 'application/json' },
                accept: 'text/html',
                responseType: ResponseType.document,
                digester: this.inspectionContainer.getApplication().getDigester()
              }
            ).then((response) => {
              let htmlElem = <HTMLElement>response.data;
              this.releaseEditors();
              replaceNodeContent(this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing'), htmlElem);
              this.createEditors();
              this.updateStatusEditor();
              this.updateMotivationOptions(this.value((<HTMLDivElement>sectionElement.querySelector('div.edit-bubbles')).dataset.result));
            }).catch((error) => {
              console.error(`Unable to visualize ticketing: Ticket ${this.ticketId} not loaded.`, error);
            });

          }
        })
      );

    }
  }
  private value(old: string): string {
    return old && old.length > 0 && old !== 'null' ? old : null;
  }

  private ownerCanChange(currentStatus: string, newStatus: string): boolean {
    return this.callbackDefinition.definition.statesMap[currentStatus]
      ? this.callbackDefinition.definition.statesMap[currentStatus].changeOwner
        ? this.callbackDefinition.definition.statesMap[currentStatus].changeOwner.indexOf(newStatus) != -1
        : false
      : false;
  }

  private resultCanChange(currentStatus: string, newStatus: string): boolean {
    return this.callbackDefinition.definition.statesMap[currentStatus]
      ? this.callbackDefinition.definition.statesMap[currentStatus].changeResult
        ? this.callbackDefinition.definition.statesMap[currentStatus].changeResult.indexOf(newStatus) != -1
        : false : false;
  }
  private motivationCanBeSelected(result: string, motivation: string): boolean {
    return this.callbackDefinition.definition.resultsMap[result]
      ? this.callbackDefinition.definition.resultsMap[result].motivations
        ? this.callbackDefinition.definition.resultsMap[result].motivations.indexOf(motivation) != -1
        : false
      : false;
  }

  private updateStatusEditor(): void {
    let sectionElement = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing');
    if (sectionElement) {
      let editorsDiv = <HTMLDivElement>sectionElement.querySelector('div.edit-bubbles');
      const currentStatus = this.value(editorsDiv.dataset.status);
      if (currentStatus) {
        const statusControl = <HTMLSelectElement>sectionElement.querySelector('div.edit-bubbles div.tlmd-ticketing-editor.status select#ticket-status-edit');
        const ownerControl = <HTMLSelectElement>sectionElement.querySelector('div.edit-bubbles div.tlmd-ticketing-editor.status select#ticket-owner-edit');
        const resultControl = <HTMLSelectElement>sectionElement.querySelector('div.edit-bubbles div.tlmd-ticketing-editor.status select#ticket-result-edit');
        const motivationControl = <HTMLSelectElement>sectionElement.querySelector('div.edit-bubbles div.tlmd-ticketing-editor.status select#ticket-motivation-edit');
        if (statusControl) {
          const status = statusControl.value;
          if (ownerControl) ownerControl.parentElement.style.display = this.ownerCanChange(currentStatus, status) ? 'unset' : 'none';
          if (resultControl) resultControl.parentElement.style.display = this.resultCanChange(currentStatus, status) ? 'unset' : 'none';
          if (motivationControl) motivationControl.parentElement.style.display = this.resultCanChange(currentStatus, status) ? 'unset' : 'none';
          if (resultControl) {
            this.updateMotivationOptions(this.value(resultControl.value));
          }
        }
      }
    }
  }

  private updateMotivationOptions(result: string): void {
    let sectionElement = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.ticketing');
    if (sectionElement) {
      let editorsDiv = <HTMLDivElement>sectionElement.querySelector('div.edit-bubbles');
      editorsDiv.querySelectorAll('div.tlmd-ticketing-editor.status select#ticket-motivation-edit option').forEach((option: HTMLOptionElement) => {
        option.style.display = this.motivationCanBeSelected(result, option.value) ? 'unset' : 'none';
      });
    }
  }

}